// Localização: frontend/src/components/Prontuario/PacienteContasReceberTab.jsx
// Aba para gerenciar contas a receber do paciente

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useContasReceber } from '../../hooks/useFinanceiro';
import { usePacientes } from '../../hooks/usePacientes';
import { usePermissions } from '../../context/PermissionContext';
import { useNotification } from '../../context/NotificationContext';
import FormContaReceberPaciente from './FormContaReceberPaciente';
import ListaContasReceber from '../Financeiro/ListaContasReceber';
import ModalRecebimento from '../Financeiro/ModalRecebimento';
import PrimaryButton from '../Common/PrimaryButton';
import Alert from '../UI/Alert';
import PeriodoFilterButton from './PeriodoFilterButton';
import StatusFilterButton from './StatusFilterButton';
import LoadingIndicator from '../Common/LoadingIndicator';
import { Plus, HandCoins } from 'lucide-react';

const PacienteContasReceberTab = ({ pacienteId }) => {
    const { checkPermission } = usePermissions();
    const { fetchPacienteById } = usePacientes();
    const { success, error: showError, warning } = useNotification();
    
    const {
        contas,
        isLoading,
        pagination,
        fetchContas,
        deleteConta,
        receber,
        receberParcial
    } = useContasReceber();

    // Estados de UI
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalRecebimentoOpen, setIsModalRecebimentoOpen] = useState(false);
    const [contaParaReceber, setContaParaReceber] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pacienteData, setPacienteData] = useState(null);
    const [periodo, setPeriodo] = useState('');
    const [periodoStartDate, setPeriodoStartDate] = useState('');
    const [periodoEndDate, setPeriodoEndDate] = useState('');
    const [statusFiltro, setStatusFiltro] = useState(['Pendente', 'Vencido']);
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null
    });
    
    // Ref para evitar chamadas duplicadas durante o carregamento
    const isLoadingRef = useRef(false);
    const debounceTimerRef = useRef(null);
    const lastParamsRef = useRef(null);

    // Permissões
    const canCreate = checkPermission('financeiro', 'can_create') !== false;
    const canDelete = checkPermission('financeiro', 'can_delete') !== false;
    const canAccess = checkPermission('financeiro', 'can_access') !== false;

    // Função para carregar dados do paciente
    const carregarDadosPaciente = useCallback(async () => {
        if (pacienteId) {
            try {
                const paciente = await fetchPacienteById(pacienteId);
                setPacienteData(paciente);
            } catch (error) {
                console.error('Erro ao carregar dados do paciente:', error);
            }
        }
    }, [pacienteId, fetchPacienteById]);

    // Função para carregar contas do paciente
    const carregarContas = useCallback(() => {
        if (canAccess && pacienteId) {
            // Priorizar período sobre filtros
            const startDate = periodoStartDate || filters.startDate || null;
            const endDate = periodoEndDate || filters.endDate || null;
            
            const params = {
                page: currentPage,
                limit: 20,
                paciente_id: pacienteId,
                data_inicio: startDate,
                data_fim: endDate
            };

            // Adicionar filtro de status se houver status selecionados
            if (statusFiltro && statusFiltro.length > 0) {
                params.status = statusFiltro.join(',');
            }

            fetchContas(params);
        }
    }, [currentPage, pacienteId, canAccess, fetchContas, periodoStartDate, periodoEndDate, filters.startDate, filters.endDate, statusFiltro]);

    // Carregar dados do paciente ao montar
    useEffect(() => {
        carregarDadosPaciente();
    }, [carregarDadosPaciente]);

    // Carregar dados ao montar ou quando mudar as dependências relevantes
    useEffect(() => {
        // Limpar timer anterior se existir
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Evitar chamadas duplicadas durante o carregamento
        if (isLoadingRef.current) {
            return;
        }
        
        if (canAccess && pacienteId && fetchContas) {
            // Priorizar período sobre filtros
            const startDate = periodoStartDate || filters.startDate || null;
            const endDate = periodoEndDate || filters.endDate || null;
            
            const params = {
                page: currentPage,
                limit: 20,
                paciente_id: pacienteId,
                data_inicio: startDate,
                data_fim: endDate
            };

            // Adicionar filtro de status se houver status selecionados
            if (statusFiltro && statusFiltro.length > 0) {
                params.status = statusFiltro.join(',');
            }

            // Verificar se os parâmetros mudaram para evitar requisições duplicadas
            const paramsKey = JSON.stringify(params);
            if (lastParamsRef.current === paramsKey) {
                return;
            }
            lastParamsRef.current = paramsKey;

            // Debounce para evitar múltiplas requisições rápidas
            debounceTimerRef.current = setTimeout(() => {
                // Verificar novamente se ainda está carregando antes de fazer a requisição
                if (!isLoadingRef.current) {
                    isLoadingRef.current = true;
                    fetchContas(params).finally(() => {
                        isLoadingRef.current = false;
                    });
                }
            }, 500); // 500ms de debounce - aumentado para evitar requisições muito rápidas
        }

        // Cleanup do timer
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pacienteId, canAccess, periodoStartDate, periodoEndDate, filters.startDate, filters.endDate, statusFiltro]);

    // Handler para mudanças no período (PeriodoFilterButton)
    const handlePeriodoChange = useCallback((newPeriodo) => {
        const periodoStr = newPeriodo || '';
        // Evitar atualização se o período é o mesmo
        if (periodo === periodoStr) {
            return;
        }
        setPeriodo(periodoStr);
    }, [periodo]);

    const handleDateRangeChange = useCallback((newStartDate, newEndDate) => {
        // Evitar atualização se os valores são os mesmos
        const startDateStr = newStartDate || '';
        const endDateStr = newEndDate || '';
        
        if (periodoStartDate === startDateStr && periodoEndDate === endDateStr) {
            return;
        }
        
        setPeriodoStartDate(startDateStr);
        setPeriodoEndDate(endDateStr);
        setCurrentPage(1);
        // Resetar referência de parâmetros para forçar nova requisição
        lastParamsRef.current = null;
    }, [periodoStartDate, periodoEndDate]);

    // Handler para mudanças no status
    const handleStatusChange = useCallback((newStatus) => {
        setStatusFiltro(newStatus || []);
        setCurrentPage(1);
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Tem certeza que deseja cancelar esta conta a receber?')) {
            return;
        }
        try {
            await deleteConta(id);
            success('Conta a receber cancelada com sucesso!');
            carregarContas();
        } catch (error) {
            showError(error.message || 'Erro ao cancelar conta a receber.');
        }
    };

    const handleReceber = (conta) => {
        setContaParaReceber(conta);
        setIsModalRecebimentoOpen(true);
    };

    const handleConfirmarRecebimento = async (contaId, tipo, data) => {
        try {
            if (tipo === 'parcial') {
                await receberParcial(contaId, data);
            } else {
                await receber(contaId, data);
            }
            success('Recebimento registrado com sucesso!');
            setIsModalRecebimentoOpen(false);
            setContaParaReceber(null);
            carregarContas();
        } catch (error) {
            showError(error.message || 'Erro ao registrar recebimento.');
        }
    };

    const handleSuccess = () => {
        carregarContas();
    };

    if (!canAccess) {
        return (
            <Alert 
                type="warning" 
                message="Você não tem permissão para acessar esta funcionalidade." 
            />
        );
    }

    return (
        <div className="space-y-6 w-full max-w-full overflow-x-hidden">
            {/* Cabeçalho */}
            <div className="flex items-center justify-end gap-2">
                <PeriodoFilterButton
                    periodo={periodo}
                    startDate={periodoStartDate}
                    endDate={periodoEndDate}
                    onPeriodoChange={handlePeriodoChange}
                    onDateRangeChange={handleDateRangeChange}
                />
                <StatusFilterButton
                    status={statusFiltro}
                    onStatusChange={handleStatusChange}
                    type="receber"
                />
                {canCreate && (
                    <PrimaryButton
                        onClick={() => setIsModalOpen(true)}
                        className="flex items-center gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        Nova Conta a Receber
                    </PrimaryButton>
                )}
            </div>

            {/* Lista de contas */}
            {isLoading && contas.length === 0 && !pagination ? (
                <div className="flex justify-center items-center py-12">
                    <LoadingIndicator size="md" />
                </div>
            ) : !isLoading && contas.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-[0.5px] border-gray-200">
                    <HandCoins className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">
                        Nenhuma conta a receber encontrada para este paciente.
                    </p>
                </div>
            ) : (
                <ListaContasReceber
                    contas={contas}
                    isLoading={isLoading}
                    onDelete={handleDelete}
                    onReceber={handleReceber}
                    pagination={pagination}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                    canDelete={canDelete}
                    receber={receber}
                    receberParcial={receberParcial}
                    onRefresh={carregarContas}
                />
            )}

            {/* Modal de formulário */}
            <FormContaReceberPaciente
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                pacienteId={pacienteId}
                pacienteNome={pacienteData?.nome_completo}
                onSuccess={handleSuccess}
            />

            {/* Modal de recebimento */}
            <ModalRecebimento
                isOpen={isModalRecebimentoOpen}
                onClose={() => {
                    setIsModalRecebimentoOpen(false);
                    setContaParaReceber(null);
                }}
                conta={contaParaReceber}
                onReceber={handleConfirmarRecebimento}
            />
        </div>
    );
};

export default PacienteContasReceberTab;

